import { Star, Quote, ChevronLeft, ChevronRight, Clock, Shield, CheckCircle } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import reviewsBackground from '@/assets/reviews-background.jpg';
const reviews = [{
  name: "Sarah M.",
  location: "Surfers Paradise",
  rating: 5,
  text: "Absolutely fantastic service! My car broke down at work and they came out within 30 minutes. Professional, honest pricing, and fixed the issue on the spot. Highly recommend Carguroo!",
  service: "Mobile Repair"
}, {
  name: "David K.",
  location: "Broadbeach",
  rating: 5,
  text: "Used Carguroo for a pre-purchase inspection on a used car. Thorough report saved me from making a costly mistake. The mechanic was knowledgeable and explained everything clearly.",
  service: "Purchase Inspection"
}, {
  name: "Lisa T.",
  location: "Robina",
  rating: 5,
  text: "Regular servicing at my home - so convenient! No more waiting at workshops. Quality service, genuine parts, and the digital service records are a great touch. Will use again.",
  service: "Car Servicing"
}, {
  name: "Michael R.",
  location: "Burleigh Heads",
  rating: 5,
  text: "Car wouldn't start on a Sunday morning. Carguroo came out and diagnosed the problem quickly. New battery installed and back on the road in no time. Great weekend service!",
  service: "Diagnostics & Repair"
}, {
  name: "Emma L.",
  location: "Southport",
  rating: 5,
  text: "Fantastic experience from start to finish. Online booking was easy, clear communication, arrived on time, and very competitive pricing. The mechanic was friendly and professional.",
  service: "Mobile Service"
}, {
  name: "James H.",
  location: "Coolangatta",
  rating: 5,
  text: "Been using Carguroo for over a year now. Consistent quality service, transparent pricing, and they always explain what needs doing. Trust them completely with my family's cars.",
  service: "Regular Customer"
}, {
  name: "Kelly W.",
  location: "Mermaid Beach",
  rating: 5,
  text: "Emergency brake repair on a Friday afternoon - they saved my weekend! Professional service, competitive pricing, and completed the repair within 2 hours. Excellent customer service!",
  service: "Brake Repair"
}, {
  name: "Peter S.",
  location: "Varsity Lakes",
  rating: 5,
  text: "Third time using Carguroo for different vehicles. Always reliable, punctual, and fair pricing. The mobile service is incredibly convenient - highly recommend to anyone!",
  service: "Multiple Services"
}, {
  name: "Amanda F.",
  location: "Palm Beach",
  rating: 5,
  text: "Battery replacement service was outstanding. Called in the morning, mechanic arrived within 2 hours with the right battery. Quick, professional, and very reasonably priced!",
  service: "Battery Replacement"
}, {
  name: "Robert C.",
  location: "Nerang",
  rating: 5,
  text: "Comprehensive car service at my workplace. Detailed report, genuine parts used, and completed on time. The convenience of not having to take time off work is incredible!",
  service: "Workplace Service"
}, {
  name: "Sophie T.",
  location: "Currumbin",
  rating: 5,
  text: "Pre-purchase inspection was thorough and professional. The detailed report with photos helped me negotiate a better price. Saved me thousands! Worth every dollar.",
  service: "Purchase Inspection"
}, {
  name: "Mark D.",
  location: "Ashmore",
  rating: 5,
  text: "Outstanding diagnostic service! Complex electrical issue resolved quickly. The mechanic explained everything clearly and provided excellent value for money. Will definitely use again.",
  service: "Electrical Diagnostics"
}];
const StarRating = ({ rating }: { rating: number }) => (
  <div className="flex items-center space-x-1">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
    ))}
  </div>
);
export const ReviewsSection = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const isMobileFromHook = useIsMobile();
  const [isMobile, setIsMobile] = useState(false);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Update local mobile state when hook changes
  useEffect(() => {
    const wasMobile = isMobile;
    setIsMobile(isMobileFromHook);

    // Reset slide if screen size changed to prevent invalid slide index
    if (wasMobile !== isMobileFromHook) {
      setCurrentSlide(0);
    }
  }, [isMobileFromHook, isMobile]);

  const reviewsPerSlide = isMobile ? 1 : 3;
  const totalSlides = Math.ceil(reviews.length / reviewsPerSlide);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => (prev + 1) % totalSlides);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isMobile || isTransitioning) return;
    
    const touchDiff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum distance for a swipe

    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe left - next slide
        nextSlide();
      } else {
        // Swipe right - previous slide
        prevSlide();
      }
    }
  };

  const getCurrentReviews = () => {
    const startIndex = currentSlide * reviewsPerSlide;
    return reviews.slice(startIndex, startIndex + reviewsPerSlide);
  };
  return <section 
    id="reviews" 
    className="py-12 lg:py-20 relative bg-gradient-to-br from-background via-muted/10 to-primary/5 mobile-section-spacing"
  >
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 hero-pattern opacity-20"></div>
    
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
            <Star className="w-8 h-8 text-primary fill-primary" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 tracking-tight">
            Why Locals Trust Carguroo
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            Real reviews from real customers across the Gold Coast who experienced our professional mobile mechanic services
          </p>
        </div>

        <div className="text-center mb-16">
          <div className="bg-card/50 backdrop-blur-sm border border-border/50 p-8 rounded-2xl shadow-xl inline-block hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center justify-center space-x-6 mb-4">
              <StarRating rating={5} />
              <span className="text-3xl font-bold text-foreground">5.0</span>
            </div>
            <p className="text-muted-foreground font-medium">Based on 150+ Google Reviews</p>
            <div className="flex items-center justify-center mt-4 space-x-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              <span className="text-sm text-green-600 font-medium">Verified Reviews</span>
            </div>
          </div>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons - Hidden on mobile */}
          {!isMobile && (
            <>
              <button 
                onClick={prevSlide} 
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110" 
                disabled={totalSlides <= 1}
                aria-label="Previous reviews"
              >
                <ChevronLeft className="w-6 h-6 text-primary" />
              </button>
              
              <button 
                onClick={nextSlide} 
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110" 
                disabled={totalSlides <= 1}
                aria-label="Next reviews"
              >
                <ChevronRight className="w-6 h-6 text-primary" />
              </button>
            </>
          )}

          {/* Reviews Grid with Touch Support */}
          <div 
            className="overflow-hidden mx-4 sm:mx-12"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
              {getCurrentReviews().map((review, index) => (
                <div 
                  key={`${currentSlide}-${index}`} 
                  className="bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl p-4 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 group relative w-full"
                >
                  <Quote className="absolute top-6 right-6 w-10 h-10 text-primary/15 group-hover:text-primary/25 transition-colors duration-300" />
                  
                  <StarRating rating={review.rating} />
                  
                  <blockquote className="text-foreground leading-relaxed my-6 text-sm sm:text-lg italic font-medium">
                    "{review.text}"
                  </blockquote>
                  
                  <div className="border-t border-border/30 pt-6 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-bold text-lg">{review.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="font-bold text-foreground text-base sm:text-lg">{review.name}</div>
                        <p className="text-xs sm:text-sm text-muted-foreground font-medium">{review.location}</p>
                      </div>
                    </div>
                    <span className="text-xs bg-primary/15 text-primary px-3 py-2 rounded-full font-semibold hover:bg-primary/25 transition-colors duration-200">
                      {review.service}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center mt-8 space-x-3">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button 
                key={index} 
                onClick={() => setCurrentSlide(index)} 
                className={`w-6 h-6 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'bg-primary scale-125' : 'bg-primary/30 hover:bg-primary/50'
                }`} 
                aria-label={`Go to review slide ${index + 1} of ${totalSlides}`}
              />
            ))}
          </div>

          {/* Mobile Swipe Hint */}
          {isMobile && totalSlides > 1 && (
            <div className="text-center mt-4">
              <p className="text-sm text-muted-foreground">
                👆 Swipe left or right to see more reviews
              </p>
            </div>
          )}
        </div>
        
        <div className="text-center mt-16 space-y-8">
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-3xl p-12 border border-primary/10">
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Ready to Join Our Happy Customers?
            </h3>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Experience Gold Coast's most trusted mobile mechanic service. Professional, convenient, and reliable.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button onClick={() => navigate('/book-now')} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-10 py-4 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg shadow-md min-w-[200px]">
                BOOK YOUR SERVICE
              </button>
              <button onClick={() => navigate('/get-quote')} className="bg-card hover:bg-card/80 border-2 border-primary text-primary hover:text-primary font-bold px-10 py-4 rounded-xl transition-all duration-300 hover:shadow-lg shadow-md min-w-[200px]">
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </div>
  </section>;
};